import _moment, { MomentInput } from 'moment';
import { Moment } from 'moment';
import 'moment-timezone';
// @ts-ignore
import { extendMoment } from 'moment-range';
import { TIME_ZONES, TIME_ZONE_KEYS } from './time-zones';

const SERVER_UTC_TIME_FORMAT: string = 'YYYY-MM-DDTHH:mm:ss[Z]';
export const DATE_TIME_FORMAT: string = 'YYYY-MM-DD HH:mm:ss';
export const TIME_FORMAT: string = 'HH:mm:ss';
export const SERVER_DATE_FORMAT: string = 'YYYY-MM-DD';
export const DATE_FORMAT: string = 'DD-MM-YYYY';
export const FORM_DATE_FORMAT: string = 'MM-DD-YYYY';
export const USER_FORM_DATE_FORMAT: string = 'LL';
export const USER_DATE_FORMAT: string = 'MMM DD, YYYY';
export const USER_TIME_FORMAT: string = 'h:mm a z';
export const USER_DATE_TIME_FORMAT: string = 'MMM DD YYYY, h:mm a z';
export const USER_FRIENDLY_DATE_TIME_FORMAT: string = 'MMM DD [at] hh:mm A (z)';
export const AUDIT_LOG_DATE_TIME_FORMAT = 'MM/DD/YYYY h:mm z';

const moment = extendMoment(_moment);

export interface IMoment extends Moment {
  mapTz(timeZone: string): IMoment;
  localOffsetInSeconds(): number;
  formatToServerUtc(): string;
  dateInLocalTimeZone(date: string): IMoment;
}

export interface IMomentRange {
  start: IMoment;
  end: IMoment;
  contains: (arg: Moment) => boolean;
}

export function normalizeTimeZone(tz: string): string {
  const mappedTz: string = TIME_ZONES[tz as TIME_ZONE_KEYS];
  return mappedTz ? mappedTz : tz;
}

moment.fn.mapTz = function (timeZone: string): IMoment {
  return this.tz(normalizeTimeZone(timeZone));
};

moment.mapTz = function (...args: string[]): IMoment {
  args[args.length - 1] = normalizeTimeZone(args[args.length - 1]);
  return moment.tz(...args);
};

moment.localOffsetInSeconds = function (): number {
  return moment().utcOffset() * 60;
};

moment.fn.formatToServerUtc = function (): string {
  return this.clone().utc().format(SERVER_UTC_TIME_FORMAT);
};

moment.dateInLocalTimeZone = function (date: string): IMoment {
  return moment.tz(date, SERVER_DATE_FORMAT, moment.tz.guess());
};

export { moment };

export function getDateAfterWeeks(
  numOfWeeks: number,
  startFromDay: number | null = null, // https://momentjs.com/docs/#/get-set/day/
): Moment {
  const dateAfterWeeks: Moment = moment().add(7 * numOfWeeks, 'days');
  return startFromDay !== null ? dateAfterWeeks.startOf('week').day(startFromDay) : dateAfterWeeks;
}

export function getPastSunday(): IMoment {
  const currentWeekSunday: number = 0;
  return moment().day(currentWeekSunday);
}

export function sortByAscTime(arr: any[], sortKey: string): any[] {
  return arr.sort((a, b) => a[sortKey].diff(b[sortKey]));
}

export function constrain(
  date: MomentInput,
  { noEarlierThan = moment(date), noLaterThan = moment(date) }:
  { noEarlierThan?: MomentInput; noLaterThan?: MomentInput } = {},
): Moment {
  if (moment(date).isBefore(noEarlierThan)) return moment(noEarlierThan);
  if (moment(date).isAfter(noLaterThan)) return moment(noLaterThan);
  return moment(date);
}

export function getUserTimeZoneDateTime(dateTime: Moment, timeZone: string, format: string = USER_FRIENDLY_DATE_TIME_FORMAT): string {
  const dateTimeUserTimeZone: Moment = dateTime.tz(timeZone);

  return dateTimeUserTimeZone.format(format);
}